import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';

const FORM_URL = 'https://readdy.ai/api/form/db73pln799gd4v2febgg';

const contactInfo = [
  { icon: 'ri-mail-line', label: '反馈邮箱', value: 'hello@adultdrama-demo.com' },
  { icon: 'ri-customer-service-2-line', label: '内容合作', value: 'press@adultdrama-demo.com' },
  { icon: 'ri-time-line', label: '响应时间', value: '工作日 10:00 - 18:00' },
  { icon: 'ri-map-pin-line', label: '办公地点', value: '示例地址 · 仅供演示' },
];

const socials = [
  { icon: 'ri-weibo-fill', label: '微博' },
  { icon: 'ri-wechat-fill', label: '微信公众号' },
  { icon: 'ri-video-line', label: '视频号' },
  { icon: 'ri-bookmark-2-line', label: '豆瓣' },
];

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setStatusMessage('感谢您的留言，我们会尽快与您联系。');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const strValue = String(value).trim();
      if (strValue) payload.append(key, strValue);
    });

    setStatus('submitting');
    setStatusMessage('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setStatusMessage('感谢您的留言，我们会尽快与您联系。');
        form.reset();
      } else {
        setStatus('error');
        setStatusMessage(String(serverMsg) || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setStatusMessage('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
          <Link to="/" className="hover:text-primary-400 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-300">联系我们</span>
        </nav>

        <div className="mb-8 max-w-2xl">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-50 font-heading">联系我们</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-400 leading-relaxed">
            如果你有内容推荐、合作意向或使用反馈，欢迎通过下方表单或联系方式与我们取得联系。我们珍视每一条建议。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <form
              data-readdy-form
              id="contact-form"
              name="contact-form"
              onSubmit={handleSubmit}
              className="rounded-xl border border-background-800 bg-background-900/60 p-6 md:p-8"
            >
              <h2 className="text-lg font-bold text-foreground-50">在线留言</h2>
              <p className="mt-1.5 text-sm text-foreground-500">带 * 为必填项</p>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm text-foreground-300 mb-2">姓名 *</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入您的称呼"
                    className="w-full rounded-md bg-background-950 border border-background-700 px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm text-foreground-300 mb-2">邮箱 *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入您的邮箱"
                    className="w-full rounded-md bg-background-950 border border-background-700 px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="subject" className="block text-sm text-foreground-300 mb-2">主题</label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="例如：内容推荐 / 合作咨询 / 使用反馈"
                  className="w-full rounded-md bg-background-950 border border-background-700 px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
                />
              </div>

              <div className="mt-5">
                <label htmlFor="message" className="block text-sm text-foreground-300 mb-2">留言内容 *</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请输入您的留言（最多 500 字）"
                  className="w-full resize-none rounded-md bg-background-950 border border-background-700 px-4 py-2.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none"
                />
              </div>

              <div className="contact-form-field">
                <label htmlFor="website_alt">请勿填写此项</label>
                <input
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-center gap-4">
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md bg-primary-600 px-7 py-3 text-sm font-semibold text-background-50 hover:bg-primary-700 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-fill" />
                  {status === 'submitting' ? '提交中…' : '提交留言'}
                </button>
                {status === 'success' && (
                  <p className="text-sm text-accent-300">
                    <i className="ri-checkbox-circle-fill mr-1.5" />
                    {statusMessage}
                  </p>
                )}
                {status === 'error' && (
                  <p className="text-sm text-primary-300">
                    <i className="ri-error-warning-fill mr-1.5" />
                    {statusMessage}
                  </p>
                )}
              </div>
            </form>
          </div>

          <div className="lg:col-span-1 flex flex-col gap-5">
            <div className="rounded-xl border border-background-800 bg-background-900/60 p-6">
              <h2 className="text-lg font-bold text-foreground-50 mb-5">联系方式</h2>
              <ul className="space-y-4">
                {contactInfo.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100/15 text-primary-300">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{item.label}</p>
                      <p className="mt-0.5 text-sm text-foreground-100 break-all">{item.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl border border-background-800 bg-background-900/60 p-6">
              <h2 className="text-lg font-bold text-foreground-50 mb-5">关注我们</h2>
              <div className="grid grid-cols-2 gap-3">
                {socials.map((s) => (
                  <span
                    key={s.label}
                    className="flex items-center gap-2 rounded-md border border-background-700 bg-background-950 px-3 py-2.5 text-sm text-foreground-300 hover:border-primary-500 hover:text-primary-300 transition-colors cursor-pointer"
                  >
                    <i className={`${s.icon} text-base`} />
                    {s.label}
                  </span>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-foreground-500">
                本页面为演示用途，联系方式与社交账号均为示例信息，提交的留言数据仅用于演示表单流程。
              </p>
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}